/* iter134 · Monetization-Modus-Selector für Post-Composer.
 * 4 Modi: FREE / SUBSCRIBERS_ONLY / SINGLE_BUY / PPV.
 * iter213 · Da es nur EIN Tier („PREMIUM") gibt, entfällt der separate
 * „Sichtbarkeit"-Toggle (TierPaywallSelector). Bei SUBSCRIBERS_ONLY oder PPV
 * wird `visible_tiers` automatisch auf `["PREMIUM"]` gesetzt — bei FREE oder
 * SINGLE_BUY auf `[]`. Der Auswahl-Modus impliziert damit die Sichtbarkeit.
 */
import { Globe, Users, Tag, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

const MODES = [
  { key: "FREE", label: "Öffentlich", desc: "Für alle sichtbar", icon: Globe },
  { key: "SUBSCRIBERS_ONLY", label: "Nur Abonnenten", desc: "Sichtbar mit Premium-Abo", icon: Users },
  { key: "SINGLE_BUY", label: "Einzelkauf", desc: "Freischaltung durch Einmal-Kauf", icon: Tag },
  { key: "PPV", label: "PPV (Abo + Einzelkauf)", desc: "Abonnenten frei · sonst einzeln kaufbar", icon: Zap },
];

export default function MonetizationModeSelector({
  mode, onModeChange,
  tiers, onTiersChange,
  price, onPriceChange,
  testIdPrefix = "post-monetization",
  // iter232 · Paid-Media-Composer soll FREE ausblenden (Backend lehnt es mit
  // INVALID_MODE ab). Standard: alle 4 Modi.
  allowedModes = null,
  showPriceField = true,
}) {
  const { t } = useTranslation();
  const visibleModes = allowedModes ? MODES.filter((m) => allowedModes.includes(m.key)) : MODES;

  const handleModeChange = (nextMode) => {
    // iter213 · Tiers implizit aus dem Modus ableiten.
    if (nextMode === "SUBSCRIBERS_ONLY" || nextMode === "PPV") {
      onTiersChange?.(["PREMIUM"]);
    } else {
      onTiersChange?.([]);
    }
    onModeChange(nextMode);
  };

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <label className="block text-[10px] uppercase tracking-wider text-[#999] mb-1">{t("studio_monet.visibility_price")}</label>
      <div className="grid grid-cols-2 gap-2">
        {visibleModes.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              data-testid={`${testIdPrefix}-mode-${m.key}`}
              onClick={() => handleModeChange(m.key)}
              className={`flex flex-col items-start gap-1 p-3 rounded-2xl border text-left transition-colors ${active ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] hover:bg-[#FAFAFA]"}`}
            >
              <Icon size={14} className="text-[#0A0A0A]" />
              <span className="text-xs font-semibold text-[#0A0A0A]">{t(`studio_monet.mode_${m.key}_label`, { defaultValue: m.label })}</span>
              <span className="text-[10px] text-[#666] leading-tight">{t(`studio_monet.mode_${m.key}_desc`, { defaultValue: m.desc })}</span>
            </button>
          );
        })}
      </div>
      {/* iter213 · Kein TierPaywallSelector mehr — Sichtbarkeit ist durch Modus impliziert. */}
      {showPriceField && (mode === "SINGLE_BUY" || mode === "PPV") && (
        <div className="pt-2">
          <label className="block text-[10px] uppercase tracking-wider text-[#999] mb-1">{t("studio_monet.price_eur")}</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            data-testid={`${testIdPrefix}-price`}
            value={price ?? ""}
            onChange={(e) => onPriceChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
            placeholder="z. B. 9,99"
            className="w-full h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          />
        </div>
      )}
    </div>
  );
}
